import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowPathIcon, ArrowsUpDownIcon, Bars3Icon, ChevronDownIcon, ChevronLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { shoppingListsAPI } from '../services/api';
import StatusToast from './StatusToast';

const SWIPE_ACTION_THRESHOLD = 88;
const SWIPE_MAX_DISTANCE = 132;
const HISTORY_WINDOW_DAYS = 60;
const ITEM_SORT_OPTIONS_DESKTOP = [
  { value: 'list-order', label: 'List order' },
  { value: 'category-asc', label: 'Category (A-Z)' },
  { value: 'category-desc', label: 'Category (Z-A)' },
  { value: 'name-asc', label: 'Item name (A-Z)' },
  { value: 'amount-desc', label: 'Est. amount (high-low)' },
];
const ITEM_SORT_OPTIONS_MOBILE = ITEM_SORT_OPTIONS_DESKTOP.filter((option) => option.value !== 'list-order');

const formatQuantity = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 1;
  }
  if (Math.abs(parsed - Math.round(parsed)) < 0.001) {
    return Math.round(parsed);
  }
  return Number(parsed.toFixed(2));
};

const formatCurrency = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return '$0.00';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(parsed);
};

const formatShortDate = (value) => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(parsed);
};

const parseManualAddInput = (value) => {
  const raw = String(value || '').trim().replace(/\s+/g, ' ');
  if (!raw) {
    return { itemName: '', quantity: null };
  }

  const leadingQuantityMatch = raw.match(/^(\d+(?:\.\d+)?)\s+(.+)$/);
  if (leadingQuantityMatch) {
    return {
      itemName: String(leadingQuantityMatch[2] || '').trim(),
      quantity: formatQuantity(Number(leadingQuantityMatch[1])),
    };
  }

  const trailingQuantityMatch = raw.match(/^(.+?)\s+(\d+(?:\.\d+)?)$/);
  if (trailingQuantityMatch) {
    return {
      itemName: String(trailingQuantityMatch[1] || '').trim(),
      quantity: formatQuantity(Number(trailingQuantityMatch[2])),
    };
  }

  return { itemName: raw, quantity: null };
};

const normalizeShoppingItemName = (value) =>
  String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const getStrategyStatusMeta = (status) => {
  switch (status) {
    case 'over-budget':
      return {
        badgeClassName: 'border-rose-200 bg-rose-50 text-rose-700',
        panelClassName: 'border-rose-200 bg-gradient-to-br from-rose-50 to-orange-50',
        accentClassName: 'text-rose-700',
        label: 'Over budget',
      };
    case 'near-limit':
      return {
        badgeClassName: 'border-amber-200 bg-amber-50 text-amber-700',
        panelClassName: 'border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50',
        accentClassName: 'text-amber-700',
        label: 'Near limit',
      };
    case 'on-track':
      return {
        badgeClassName: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        panelClassName: 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-cyan-50',
        accentClassName: 'text-emerald-700',
        label: 'On track',
      };
    case 'no-budget':
    default:
      return {
        badgeClassName: 'border-slate-200 bg-slate-100 text-slate-700',
        panelClassName: 'border-slate-200 bg-gradient-to-br from-slate-50 to-white',
        accentClassName: 'text-slate-700',
        label: 'No budget',
      };
  }
};

const getStrategyHeadline = (strategyOverall) => {
  if (!strategyOverall?.hasBudget) {
    return {
      eyebrow: 'No guardrail yet',
      title: 'Set a budget to unlock checkout warnings',
      detail: 'You already have the cart estimate. Add a monthly budget and this list can warn you before spending slips.',
    };
  }

  if (strategyOverall?.status === 'over-budget') {
    return {
      eyebrow: 'Pause before checkout',
      title: `This trip puts you over by ${formatCurrency(strategyOverall?.projectedOverspend || 0)}`,
      detail: 'Trim a few items now and you can avoid breaking this month’s plan at the register.',
    };
  }

  if (strategyOverall?.status === 'near-limit') {
    return {
      eyebrow: 'Close to the line',
      title: `${formatCurrency(strategyOverall?.projectedRemaining || 0)} left after checkout`,
      detail: 'You can make this trip work, but there is not much room for price changes or extras.',
    };
  }

  return {
    eyebrow: 'Safe to buy',
    title: `${formatCurrency(strategyOverall?.projectedRemaining || 0)} left after checkout`,
    detail: 'This cart still fits the budget you set for this month.',
  };
};

function ShoppingListDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [shoppingList, setShoppingList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newItemName, setNewItemName] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState('');
  const [newItemEstimatedAmount, setNewItemEstimatedAmount] = useState('');
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [pendingItemIds, setPendingItemIds] = useState([]);
  const [editingItemId, setEditingItemId] = useState('');
  const [itemNameDraft, setItemNameDraft] = useState('');
  const [editingEstimatedItemId, setEditingEstimatedItemId] = useState('');
  const [estimatedAmountDraft, setEstimatedAmountDraft] = useState('');
  const [reordering, setReordering] = useState(false);
  const [draggingItemId, setDraggingItemId] = useState('');
  const [itemSortMode, setItemSortMode] = useState('list-order');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );
  const [smartHistoryItems, setSmartHistoryItems] = useState([]);
  const [loadingSmartHistoryItems, setLoadingSmartHistoryItems] = useState(false);
  const [addingHistoryItemKey, setAddingHistoryItemKey] = useState('');
  const [showCheckedItems, setShowCheckedItems] = useState(false);
  const [showStrategyCard, setShowStrategyCard] = useState(false);
  const [showStrategyDetails] = useState(false);
  const [swipeState, setSwipeState] = useState({
    itemId: '',
    offsetX: 0,
    isDragging: false,
  });
  const [showScrollActions, setShowScrollActions] = useState(false);
  const [showFloatingAddButton, setShowFloatingAddButton] = useState(false);
  const lastScrollYRef = useRef(0);
  const addSectionRef = useRef(null);
  const addInputRef = useRef(null);
  const swipeRef = useRef({
    pointerId: null,
    itemId: '',
    startX: 0,
    startY: 0,
    offsetX: 0,
    axisLocked: false,
    horizontalSwipe: false,
  });

  const isItemPending = useCallback(
    (itemId) => pendingItemIds.includes(String(itemId)),
    [pendingItemIds]
  );

  const markItemPending = useCallback((itemId) => {
    const normalizedId = String(itemId);
    setPendingItemIds((current) => (
      current.includes(normalizedId) ? current : [...current, normalizedId]
    ));
  }, []);

  const clearItemPending = useCallback((itemId) => {
    const normalizedId = String(itemId);
    setPendingItemIds((current) => current.filter((entry) => entry !== normalizedId));
  }, []);

  const patchItemInList = useCallback((itemId, updater) => {
    const normalizedId = String(itemId);
    setShoppingList((current) => {
      if (!current || !Array.isArray(current.items)) {
        return current;
      }

      return {
        ...current,
        items: current.items.map((entry) => {
          if (String(entry.id) !== normalizedId) {
            return entry;
          }
          return typeof updater === 'function'
            ? updater(entry, current.items)
            : { ...entry, ...updater };
        }),
      };
    });
  }, []);

  const removeItemFromList = useCallback((itemId) => {
    const normalizedId = String(itemId);
    setShoppingList((current) => {
      if (!current || !Array.isArray(current.items)) {
        return current;
      }

      const nextItems = current.items.filter((entry) => String(entry.id) !== normalizedId);
      return {
        ...current,
        items: nextItems,
        itemCount: nextItems.length,
      };
    });
  }, []);

  const loadList = useCallback(async () => {
    if (!id) {
      return;
    }
    const list = await shoppingListsAPI.getById(id);
    setShoppingList(list);
  }, [id]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        await loadList();
      } catch (err) {
        console.error('Failed to load shopping list:', err);
        setError(err.response?.data?.error || 'Failed to load shopping list');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [loadList]);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    const parsedManualEntry = parseManualAddInput(newItemName);
    const searchTerm = String(parsedManualEntry.itemName || '').trim();
    if (!searchTerm) {
      setSmartHistoryItems([]);
      setLoadingSmartHistoryItems(false);
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      setLoadingSmartHistoryItems(true);
      try {
        const response = await shoppingListsAPI.historyItems(id, {
          windowDays: HISTORY_WINDOW_DAYS,
          search: searchTerm,
          limit: 6,
        });
        setSmartHistoryItems(Array.isArray(response?.items) ? response.items : []);
      } catch (err) {
        console.error('Failed to load smart history items:', err);
        setSmartHistoryItems([]);
      } finally {
        setLoadingSmartHistoryItems(false);
      }
    }, 180);

    return () => window.clearTimeout(timer);
  }, [id, newItemName]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobileView && itemSortMode === 'list-order') {
      setItemSortMode('category-asc');
    }
  }, [isMobileView, itemSortMode]);

  useEffect(() => {
    const handleDocumentClick = () => {
      setShowSortMenu(false);
    };
    if (!showSortMenu) {
      return undefined;
    }
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [showSortMenu]);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const scrollingUp = currentY < lastScrollYRef.current;
      setShowScrollActions(currentY > 120 && scrollingUp);
      lastScrollYRef.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isMobileView) {
      setShowFloatingAddButton(false);
      return undefined;
    }

    const target = addSectionRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFloatingAddButton(!entry.isIntersecting);
      },
      {
        threshold: 0.15,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [isMobileView, shoppingList?.id]);

  const scrollToAddSection = useCallback(() => {
    const section = addSectionRef.current;
    if (section) {
      const sectionTop = section.getBoundingClientRect().top + window.scrollY;
      const topOffset = isMobileView ? (showScrollActions ? 132 : 88) : 24;
      const targetTop = Math.max(0, sectionTop - topOffset);
      window.scrollTo({ top: targetTop, behavior: 'smooth' });
    }

    window.setTimeout(() => {
      addInputRef.current?.focus();
    }, 220);
  }, [isMobileView, showScrollActions]);

  const handleToggleItem = async (item) => {
    if (!shoppingList || isItemPending(item.id)) {
      return;
    }

    const previousList = shoppingList;
    const nextIsChecked = !item.isChecked;

    try {
      markItemPending(item.id);
      setError('');
      patchItemInList(item.id, (currentItem, allItems) => {
        const maxSortOrder = allItems.reduce(
          (maxValue, entry) => Math.max(maxValue, Number(entry.sortOrder || 0)),
          -1
        );

        return {
          ...currentItem,
          isChecked: nextIsChecked,
          sortOrder: nextIsChecked && !currentItem.isChecked ? maxSortOrder + 1 : currentItem.sortOrder,
        };
      });
      const updatedItem = await shoppingListsAPI.updateItem(shoppingList.id, item.id, {
        isChecked: nextIsChecked,
      });
      patchItemInList(item.id, updatedItem);
    } catch (err) {
      console.error('Failed to update item:', err);
      setShoppingList(previousList);
      setError(err.response?.data?.error || 'Failed to update item');
    } finally {
      clearItemPending(item.id);
    }
  };

  const handleAdjustQuantity = async (item, delta) => {
    if (!shoppingList || isItemPending(item.id)) {
      return;
    }

    const current = Number(item.quantity || 1);
    const next = Math.max(1, formatQuantity(current + delta));
    if (Math.abs(next - current) < 0.001) {
      return;
    }

    try {
      markItemPending(item.id);
      setError('');
      const updatedItem = await shoppingListsAPI.updateItem(shoppingList.id, item.id, {
        quantity: next,
      });
      patchItemInList(item.id, updatedItem);
    } catch (err) {
      console.error('Failed to update quantity:', err);
      setError(err.response?.data?.error || 'Failed to update quantity');
    } finally {
      clearItemPending(item.id);
    }
  };

  const openItemNameEditor = (item) => {
    if (!shoppingList || isItemPending(item.id)) {
      return;
    }
    setEditingEstimatedItemId('');
    setEstimatedAmountDraft('');
    setEditingItemId(String(item.id));
    setItemNameDraft(String(item.itemName || ''));
  };

  const cancelItemNameEditor = () => {
    setEditingItemId('');
    setItemNameDraft('');
  };

  const submitItemNameEditor = async (item) => {
    if (!shoppingList || isItemPending(item.id)) {
      return;
    }

    const trimmed = String(itemNameDraft || '').trim();
    if (!trimmed) {
      setError('Item name cannot be empty.');
      return;
    }

    try {
      markItemPending(item.id);
      setError('');
      const updatedItem = await shoppingListsAPI.updateItem(shoppingList.id, item.id, {
        itemName: trimmed,
      });
      cancelItemNameEditor();
      patchItemInList(item.id, updatedItem);
    } catch (err) {
      console.error('Failed to update item name:', err);
      setError(err.response?.data?.error || 'Failed to update item name');
    } finally {
      clearItemPending(item.id);
    }
  };

  const openEstimatedAmountEditor = (item) => {
    if (!shoppingList || isItemPending(item.id)) {
      return;
    }
    setEditingItemId('');
    setItemNameDraft('');
    setEditingEstimatedItemId(String(item.id));
    setEstimatedAmountDraft(
      item.estimatedItemAmount === null || item.estimatedItemAmount === undefined
        ? ''
        : String(item.estimatedItemAmount)
    );
  };

  const cancelEstimatedAmountEditor = () => {
    setEditingEstimatedItemId('');
    setEstimatedAmountDraft('');
  };

  const submitEstimatedAmountEditor = async (item) => {
    if (!shoppingList || isItemPending(item.id)) {
      return;
    }
    const trimmed = String(estimatedAmountDraft || '').trim();
    if (trimmed) {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setError('Estimated amount must be a positive number.');
        return;
      }
    }

    try {
      markItemPending(item.id);
      setError('');
      const updatedItem = await shoppingListsAPI.updateItem(shoppingList.id, item.id, {
        estimatedItemAmount: trimmed === '' ? null : Number(trimmed),
      });
      cancelEstimatedAmountEditor();
      patchItemInList(item.id, updatedItem);
    } catch (err) {
      console.error('Failed to update estimated amount:', err);
      setError(err.response?.data?.error || 'Failed to update estimated amount');
    } finally {
      clearItemPending(item.id);
    }
  };

  const handleAddItem = async (event) => {
    event.preventDefault();
    if (!shoppingList || addingItem) {
      return;
    }

    const parsedManualEntry = parseManualAddInput(newItemName);
    const itemName = parsedManualEntry.itemName;
    if (!itemName) {
      return;
    }
    const explicitQuantity = String(newItemQuantity || '').trim();
    const quantity = explicitQuantity
      ? Math.max(1, Number(explicitQuantity))
      : Math.max(1, Number(parsedManualEntry.quantity || 1));
    const estimatedItemAmount = String(newItemEstimatedAmount || '').trim() === ''
      ? null
      : Number(newItemEstimatedAmount);
    if (estimatedItemAmount !== null && (!Number.isFinite(estimatedItemAmount) || estimatedItemAmount <= 0)) {
      setError('Estimated amount must be a positive number.');
      return;
    }

    try {
      setAddingItem(true);
      setError('');
      const normalizedItemName = normalizeShoppingItemName(itemName);
      const existingItem = (shoppingList.items || []).find(
        (entry) => normalizeShoppingItemName(entry.itemName) === normalizedItemName
      );

      if (existingItem) {
        const nextQuantity = formatQuantity(Number(existingItem.quantity || 1) + quantity);
        const updatedItem = await shoppingListsAPI.updateItem(shoppingList.id, existingItem.id, {
          quantity: nextQuantity,
        });
        patchItemInList(existingItem.id, updatedItem);
      } else {
        const createdItem = await shoppingListsAPI.addItem(shoppingList.id, {
          itemName,
          quantity,
          estimatedItemAmount,
          source: 'MANUAL',
        });
        setShoppingList((current) => (
          current
            ? {
                ...current,
                items: [...(Array.isArray(current.items) ? current.items : []), createdItem],
                itemCount: Number(current.itemCount || 0) + 1,
              }
            : current
        ));
      }
      setNewItemName('');
      setNewItemQuantity('');
      setNewItemEstimatedAmount('');
      setShowAddItemForm(false);
      setSmartHistoryItems([]);
    } catch (err) {
      console.error('Failed to add item:', err);
      setError(err.response?.data?.error || 'Failed to add item');
    } finally {
      setAddingItem(false);
    }
  };

  const handleAddFromHistory = async (historyItem) => {
    if (!shoppingList || addingItem || addingHistoryItemKey) {
      return;
    }

    const itemName = String(historyItem?.itemName || '').trim();
    if (!itemName) {
      return;
    }

    const normalizedItemName = String(historyItem?.normalizedItemName || '').trim();
    const quantity = Math.max(1, Number(historyItem?.quantity || 1));
    const estimatedUnitAmount = historyItem?.estimatedUnitAmount === null || historyItem?.estimatedUnitAmount === undefined
      ? null
      : Number(historyItem.estimatedUnitAmount);
    const estimatedItemAmount = historyItem?.estimatedItemAmount === null || historyItem?.estimatedItemAmount === undefined
      ? null
      : Number(historyItem.estimatedItemAmount);
    const rowKey = `${normalizedItemName || itemName}::${quantity}`;

    try {
      setAddingHistoryItemKey(rowKey);
      setError('');
      const response = await shoppingListsAPI.addItemFromHistory(shoppingList.id, {
        itemName,
        normalizedItemName,
        quantity,
        estimatedUnitAmount,
        estimatedItemAmount,
        mainCategoryName: historyItem?.mainCategoryName || '',
        subCategoryName: historyItem?.subCategoryName || '',
        mergeExisting: true,
      });
      if (response?.shoppingList) {
        setShoppingList(response.shoppingList);
      } else if (response?.item && response?.merged) {
        patchItemInList(response.item.id, response.item);
      } else if (response?.item) {
        setShoppingList((current) => (
          current
            ? {
                ...current,
                items: [...(Array.isArray(current.items) ? current.items : []), response.item],
                itemCount: Number(current.itemCount || 0) + 1,
              }
            : current
        ));
      } else {
        await loadList();
      }
      setNewItemName('');
      setSmartHistoryItems([]);
    } catch (err) {
      console.error('Failed to add history item:', err);
      setError(err.response?.data?.error || 'Failed to add item from history');
    } finally {
      setAddingHistoryItemKey('');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!shoppingList || isItemPending(item.id)) {
      return;
    }

    const previousList = shoppingList;

    try {
      markItemPending(item.id);
      setError('');
      removeItemFromList(item.id);
      await shoppingListsAPI.deleteItem(shoppingList.id, item.id);
    } catch (err) {
      console.error('Failed to delete item:', err);
      setShoppingList(previousList);
      setError(err.response?.data?.error || 'Failed to delete item');
    } finally {
      clearItemPending(item.id);
    }
  };

  const handleReorderDrop = async (targetItemId) => {
    if (!shoppingList || !draggingItemId || reordering) {
      return;
    }

    const currentItems = Array.isArray(shoppingList.items) ? shoppingList.items : [];
    const fromIndex = currentItems.findIndex((item) => String(item.id) === String(draggingItemId));
    const toIndex = currentItems.findIndex((item) => String(item.id) === String(targetItemId));
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
      setDraggingItemId('');
      return;
    }

    const nextItems = [...currentItems];
    const [moved] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, moved);
    setShoppingList((current) => (
      current
        ? { ...current, items: nextItems }
        : current
    ));

    try {
      setReordering(true);
      setError('');
      await shoppingListsAPI.reorderItems(shoppingList.id, {
        itemIds: nextItems.map((item) => item.id),
      });
      await loadList();
    } catch (err) {
      console.error('Failed to reorder items:', err);
      setError(err.response?.data?.error || 'Failed to reorder items');
      await loadList();
    } finally {
      setReordering(false);
      setDraggingItemId('');
    }
  };

  const resetSwipe = useCallback(() => {
    swipeRef.current = {
      pointerId: null,
      itemId: '',
      startX: 0,
      startY: 0,
      offsetX: 0,
      axisLocked: false,
      horizontalSwipe: false,
    };
    setSwipeState({
      itemId: '',
      offsetX: 0,
      isDragging: false,
    });
  }, []);

  const handleItemPointerDown = (item, event) => {
    if (!shoppingList || pendingItemIds.length > 0) {
      return;
    }
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }
    if (event.target instanceof Element && event.target.closest('[data-no-swipe="true"]')) {
      return;
    }

    swipeRef.current = {
      pointerId: event.pointerId,
      itemId: String(item.id),
      startX: event.clientX,
      startY: event.clientY,
      offsetX: 0,
      axisLocked: false,
      horizontalSwipe: false,
    };
    setSwipeState({
      itemId: String(item.id),
      offsetX: 0,
      isDragging: false,
    });
  };

  const handleItemPointerMove = (item, event) => {
    const swipe = swipeRef.current;
    if (swipe.pointerId !== event.pointerId || swipe.itemId !== String(item.id)) {
      return;
    }

    const deltaX = event.clientX - swipe.startX;
    const deltaY = event.clientY - swipe.startY;

    if (!swipe.axisLocked && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
      swipe.axisLocked = true;
      swipe.horizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    }

    if (!swipe.axisLocked || !swipe.horizontalSwipe) {
      return;
    }

    event.preventDefault();
    const offsetX = Math.max(-SWIPE_MAX_DISTANCE, Math.min(SWIPE_MAX_DISTANCE, deltaX));
    swipe.offsetX = offsetX;

    setSwipeState({
      itemId: String(item.id),
      offsetX,
      isDragging: true,
    });
  };

  const handleItemPointerEnd = (item, event) => {
    const swipe = swipeRef.current;
    if (swipe.pointerId !== event.pointerId || swipe.itemId !== String(item.id)) {
      return;
    }

    const offsetX = swipe.offsetX;
    resetSwipe();

    if (offsetX <= -SWIPE_ACTION_THRESHOLD) {
      handleDeleteItem(item);
      return;
    }

    if (offsetX >= SWIPE_ACTION_THRESHOLD) {
      handleToggleItem(item);
    }
  };

  useEffect(() => {
    resetSwipe();
  }, [shoppingList?.id, resetSwipe]);

  const handleResetChecklist = async () => {
    if (!shoppingList) {
      return;
    }

    try {
      setError('');
      const refreshedList = await shoppingListsAPI.resetChecks(shoppingList.id);
      setShoppingList(refreshedList);
    } catch (err) {
      console.error('Failed to reset checklist:', err);
      setError(err.response?.data?.error || 'Failed to reset checklist');
    }
  };

  const handleDeleteList = async () => {
    if (!shoppingList) {
      return;
    }

    const shouldDelete = window.confirm(`Delete "${shoppingList.name}"?`);
    if (!shouldDelete) {
      return;
    }

    try {
      setError('');
      await shoppingListsAPI.delete(shoppingList.id);
      navigate('/shopping-lists');
    } catch (err) {
      console.error('Failed to delete shopping list:', err);
      setError(err.response?.data?.error || 'Failed to delete shopping list');
    }
  };

  const estimatedListTotal = Array.isArray(shoppingList?.items)
    ? shoppingList.items.reduce((sum, item) => sum + Number(item.estimatedItemAmount || 0), 0)
    : 0;
  const totalListQuantity = Array.isArray(shoppingList?.items)
    ? shoppingList.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
    : 0;
  const hasEstimatedAmounts = Array.isArray(shoppingList?.items)
    && shoppingList.items.some((item) => item.estimatedItemAmount !== null && item.estimatedItemAmount !== undefined);
  const strategySnapshot = shoppingList?.strategySnapshot || null;
  const strategyOverall = strategySnapshot?.overall || null;
  const strategyListTotals = strategySnapshot?.listTotals || null;
  const strategyCategories = Array.isArray(strategySnapshot?.categories) ? strategySnapshot.categories : [];
  const strategySummary = strategySnapshot?.summary || null;
  const overBudgetCategoryNames = strategyCategories
    .filter((category) => category.status === 'over-budget')
    .map((category) => category.mainCategoryName)
    .filter(Boolean);
  const overallStrategyMeta = getStrategyStatusMeta(strategyOverall?.status);
  const strategyHeadline = getStrategyHeadline(strategyOverall);
  const strategyTrimPlan = useMemo(() => {
    if (!strategyOverall?.hasBudget || !Array.isArray(shoppingList?.items)) {
      return null;
    }

    const trimTarget = strategyOverall.status === 'over-budget'
      ? Number(strategyOverall.projectedOverspend || 0)
      : strategyOverall.status === 'near-limit'
        ? Math.max(10, Number(strategyListTotals?.estimatedTotal || 0) * 0.1)
        : 0;

    if (trimTarget <= 0) {
      return null;
    }

    const removableItems = shoppingList.items
      .filter((item) => !item.isChecked && Number(item.estimatedItemAmount || 0) > 0)
      .sort((a, b) => Number(b.estimatedItemAmount || 0) - Number(a.estimatedItemAmount || 0));

    if (!removableItems.length) {
      return null;
    }

    const picks = [];
    let runningTotal = 0;
    for (const item of removableItems) {
      picks.push(item);
      runningTotal += Number(item.estimatedItemAmount || 0);
      if (runningTotal >= trimTarget) {
        break;
      }
    }

    return {
      target: trimTarget,
      covered: runningTotal,
      picks: picks.slice(0, 3),
    };
  }, [shoppingList?.items, strategyListTotals?.estimatedTotal, strategyOverall]);
  const itemSortOptions = isMobileView ? ITEM_SORT_OPTIONS_MOBILE : ITEM_SORT_OPTIONS_DESKTOP;
  const activeSortLabel = itemSortOptions.find((option) => option.value === itemSortMode)?.label || 'Category (A-Z)';
  const visibleItems = useMemo(() => {
    const source = Array.isArray(shoppingList?.items) ? [...shoppingList.items] : [];
    source.sort((a, b) => {
      if (Boolean(a.isChecked) !== Boolean(b.isChecked)) {
        return a.isChecked ? 1 : -1;
      }
      if (itemSortMode === 'name-asc') {
        return String(a.itemName || '').localeCompare(String(b.itemName || ''), undefined, { sensitivity: 'base' });
      }
      if (itemSortMode === 'amount-desc') {
        return Number(b.estimatedItemAmount || 0) - Number(a.estimatedItemAmount || 0);
      }
      if (itemSortMode === 'category-asc' || itemSortMode === 'category-desc') {
        const mainA = String(a.mainCategoryName || 'Uncategorized');
        const mainB = String(b.mainCategoryName || 'Uncategorized');
        const subA = String(a.subCategoryName || 'General');
        const subB = String(b.subCategoryName || 'General');
        const itemA = String(a.itemName || '');
        const itemB = String(b.itemName || '');

        const mainCompare = mainA.localeCompare(mainB, undefined, { sensitivity: 'base' });
        if (mainCompare !== 0) {
          return itemSortMode === 'category-desc' ? -mainCompare : mainCompare;
        }
        const subCompare = subA.localeCompare(subB, undefined, { sensitivity: 'base' });
        if (subCompare !== 0) {
          return itemSortMode === 'category-desc' ? -subCompare : subCompare;
        }
        const itemCompare = itemA.localeCompare(itemB, undefined, { sensitivity: 'base' });
        return itemSortMode === 'category-desc' ? -itemCompare : itemCompare;
      }
      return Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
    });
    return source;
  }, [shoppingList?.items, itemSortMode]);
  const allowManualReorder = !isMobileView && itemSortMode === 'list-order';
  const uncheckedVisibleItems = useMemo(
    () => visibleItems.filter((item) => !item.isChecked),
    [visibleItems]
  );
  const checkedVisibleItems = useMemo(
    () => visibleItems.filter((item) => item.isChecked),
    [visibleItems]
  );
  const parsedManualDraft = useMemo(() => parseManualAddInput(newItemName), [newItemName]);
  const smartAddPreviewQuantity = String(newItemQuantity || '').trim()
    ? Math.max(1, Number(newItemQuantity || 1))
    : Math.max(1, Number(parsedManualDraft.quantity || 1));
  const strategySection = strategySnapshot ? (
    <section className={`overflow-hidden rounded-[22px] border shadow-sm sm:rounded-[28px] ${overallStrategyMeta.panelClassName}`}>
      <div
        className={`${showStrategyCard ? 'border-b border-white/60' : ''} cursor-pointer px-3 py-2.5 sm:px-5 sm:py-5`}
        onClick={() => setShowStrategyCard((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setShowStrategyCard((current) => !current);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={showStrategyCard}
        aria-label={showStrategyCard ? 'Collapse shopping strategy card' : 'Expand shopping strategy card'}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {showStrategyCard && (
              <div className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${overallStrategyMeta.badgeClassName}`}>
                Shopping with Strategy
              </div>
            )}
            <p className={`${showStrategyCard ? 'mt-2' : 'mt-0.5'} max-w-2xl text-sm leading-5 text-slate-600 sm:leading-6`}>
              {strategyHeadline.detail}
            </p>
          </div>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setShowStrategyCard((current) => !current);
            }}
            aria-expanded={showStrategyCard}
            aria-label={showStrategyCard ? 'Collapse shopping strategy card' : 'Expand shopping strategy card'}
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-slate-700 transition"
          >
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform duration-200 ${showStrategyCard ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {showStrategyCard && (
        <>
          <div className="px-3 pb-3.5 sm:px-5 sm:pb-5">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-2xl border border-white/70 bg-white/90 px-3 py-2.5 shadow-sm">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Budget</p>
                <p className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">
                  {strategyOverall?.hasBudget ? formatCurrency(strategyOverall?.budgetAmount || 0) : '--'}
                </p>
              </div>
              <div className="rounded-2xl border border-white/70 bg-white/90 px-3 py-2.5 shadow-sm">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Spent so far</p>
                <p className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">
                  {strategyOverall?.hasBudget ? formatCurrency(strategyOverall?.spentAmount || 0) : '--'}
                </p>
              </div>
              <div className="rounded-2xl border border-white/70 bg-white/90 px-3 py-2.5 shadow-sm">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Cart</p>
                <p className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">
                  {formatCurrency(strategyListTotals?.estimatedTotal || 0)}
                </p>
              </div>
              <div className="rounded-2xl border border-white/70 bg-white/90 px-3 py-2.5 shadow-sm">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Remaining</p>
                <p className={`mt-1 text-base font-semibold sm:text-lg ${overallStrategyMeta.accentClassName}`}>
                  {strategyOverall?.hasBudget ? formatCurrency(strategyOverall?.projectedRemaining || 0) : '--'}
                </p>
              </div>
            </div>
            {overBudgetCategoryNames.length > 0 && (
              <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50/90 px-3 py-2 text-sm font-medium text-rose-700">
                This purchase could put {overBudgetCategoryNames.join(', ')} over budget.
              </div>
            )}
            {strategySummary && strategySummary.categoriesWithBudgetsCount > 0 && (
              <div className="mt-3 rounded-2xl border border-white/70 bg-white/90 px-3 py-2 text-sm text-slate-700">
                Tracking {strategySummary.categoriesWithBudgetsCount} budgeted categor{strategySummary.categoriesWithBudgetsCount === 1 ? 'y' : 'ies'} for this trip.
              </div>
            )}
          </div>
          {strategyTrimPlan && (
            <div className="border-b border-white/60 bg-white/75 px-4 py-4 sm:px-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Fastest way to rebalance</p>
                  <p className="mt-1 text-sm text-slate-600">
                    {strategyOverall?.status === 'over-budget'
                      ? `These items cover about ${formatCurrency(strategyTrimPlan.covered)} if you need to get back under budget quickly.`
                      : `These items free up about ${formatCurrency(strategyTrimPlan.covered)} if you want a little more cushion.`}
                  </p>
                </div>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                {strategyTrimPlan.picks.map((item) => (
                  <div key={`trim-pick-${item.id}`} className="rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
                    <p className="truncate text-sm font-semibold text-slate-900">{item.itemName}</p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {item.mainCategoryName || 'Uncategorized'} Â· Qty {formatQuantity(item.quantity)}
                    </p>
                    <p className="mt-2 text-base font-semibold text-slate-900">{formatCurrency(item.estimatedItemAmount || 0)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showStrategyDetails && (
            <div className="bg-white/80 px-4 py-4 sm:px-5 sm:py-5">
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
                <div className="rounded-2xl border border-slate-200 bg-white p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">Money flow</p>
                    <p className="text-xs text-slate-500">Before and after this trip</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                      <span className="text-sm text-slate-600">Budget left now</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {strategyOverall?.hasBudget ? formatCurrency(strategyOverall?.remainingBeforeTrip || 0) : '--'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                      <span className="text-sm text-slate-600">This cart</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {formatCurrency(strategyListTotals?.estimatedTotal || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-slate-900 px-3 py-3 text-white">
                      <span className="text-sm font-medium">Projected after checkout</span>
                      <span className="text-base font-semibold">
                        {strategyOverall?.hasBudget ? formatCurrency(strategyOverall?.projectedRemaining || 0) : '--'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">Category impact</p>
                    <p className="text-xs text-slate-500">Top pressure points</p>
                  </div>
                  <div className="space-y-2">
                    {strategyCategories.slice(0, 5).map((category) => {
                      const categoryMeta = getStrategyStatusMeta(category.status);
                      return (
                        <div
                          key={`strategy-category-${category.mainCategoryName}`}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-sm font-semibold text-slate-900">{category.mainCategoryName}</p>
                                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${categoryMeta.badgeClassName}`}>
                                  {categoryMeta.label}
                                </span>
                              </div>
                              <p className="mt-1 text-[11px] text-slate-500">
                                {category.budgetAmount > 0
                                  ? `${formatCurrency(category.spentAmount)} spent of ${formatCurrency(category.budgetAmount)}`
                                  : 'No category budget set'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-slate-900">{formatCurrency(category.listEstimatedAmount)}</p>
                              <p className={`text-[11px] font-medium ${categoryMeta.accentClassName}`}>
                                {category.budgetAmount > 0
                                  ? (category.status === 'over-budget'
                                    ? `${formatCurrency(category.projectedOverspend)} over`
                                    : `${formatCurrency(category.projectedRemaining)} left`)
                                  : 'Set budget'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  ) : null;
  const renderShoppingListItem = (item, { draggable = false } = {}) => (
    <div key={item.id} className="relative overflow-hidden rounded-xl border border-gray-200">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-between px-4">
        <div
          className={`rounded-md bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700 transition-opacity ${
            swipeState.itemId === String(item.id) && swipeState.offsetX > 12 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {item.isChecked ? 'Mark Unchecked' : 'Mark Checked'}
        </div>
        <div
          className={`rounded-md bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 transition-opacity ${
            swipeState.itemId === String(item.id) && swipeState.offsetX < -12 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          Delete Item
        </div>
      </div>

      <div
        className={`relative bg-white px-3 py-2.5 sm:px-3 sm:py-2 ${draggingItemId === String(item.id) ? 'opacity-70' : ''}`}
        style={{
          transform: swipeState.itemId === String(item.id) ? `translateX(${swipeState.offsetX}px)` : 'translateX(0px)',
          transition: swipeState.itemId === String(item.id) && swipeState.isDragging ? 'none' : 'transform 160ms ease',
          touchAction: 'pan-y',
        }}
        draggable={draggable}
        onDragStart={(event) => {
          if (!draggable) {
            event.preventDefault();
            return;
          }
          if (event.target instanceof Element && event.target.closest('[data-no-swipe="true"]')) {
            event.preventDefault();
            return;
          }
          setDraggingItemId(String(item.id));
          event.dataTransfer.effectAllowed = 'move';
        }}
        onDragOver={(event) => {
          if (!draggable) {
            return;
          }
          if (draggingItemId && draggingItemId !== String(item.id)) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
          }
        }}
        onDrop={(event) => {
          if (!draggable) {
            return;
          }
          event.preventDefault();
          handleReorderDrop(String(item.id));
        }}
        onDragEnd={() => {
          setDraggingItemId('');
        }}
        onPointerDown={(event) => handleItemPointerDown(item, event)}
        onPointerMove={(event) => handleItemPointerMove(item, event)}
        onPointerUp={(event) => handleItemPointerEnd(item, event)}
        onPointerCancel={(event) => handleItemPointerEnd(item, event)}
        onPointerLeave={(event) => {
          if (event.buttons === 0) {
            handleItemPointerEnd(item, event);
          }
        }}
      >
        <div className="flex items-center gap-2.5 sm:gap-2">
          <span
            aria-hidden="true"
            className={`hidden h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 sm:inline-flex ${draggable ? 'cursor-grab' : ''}`}
          >
            {draggable ? <Bars3Icon className="h-4 w-4" aria-hidden="true" /> : null}
          </span>
          <input
            type="checkbox"
            checked={item.isChecked}
            onChange={() => handleToggleItem(item)}
            disabled={isItemPending(item.id)}
            data-no-swipe="true"
            className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />

          <div className="min-w-0 flex-1">
            <div className="grid grid-cols-[minmax(0,1fr)_84px_78px] items-center gap-2 sm:grid-cols-[minmax(0,1fr)_96px_88px]">
              <div className="min-w-0 pr-1">
                <button
                  type="button"
                  onClick={() => openItemNameEditor(item)}
                  disabled={isItemPending(item.id)}
                  className={`w-full rounded text-left whitespace-normal break-words text-[13px] font-semibold leading-4 transition sm:text-[14px] ${
                    item.isChecked ? 'text-gray-400 line-through' : 'text-gray-900 hover:text-blue-700'
                  }`}
                >
                  {item.itemName}
                </button>
                {(item.mainCategoryName || item.subCategoryName) && (
                  <p className="mt-0.5 text-[11px] leading-4 text-slate-500">
                    {(item.mainCategoryName || 'Uncategorized')} / {(item.subCategoryName || 'General')}
                  </p>
                )}
              </div>

              <div className="justify-self-center">
                <div className="inline-flex h-8 items-center justify-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-1 sm:h-8">
                  <button
                    type="button"
                    onClick={() => handleAdjustQuantity(item, -1)}
                    disabled={isItemPending(item.id) || Number(item.quantity || 1) <= 1}
                    data-no-swipe="true"
                    className="h-6 w-6 rounded-md border border-slate-200 bg-white text-sm font-semibold text-slate-700 transition active:scale-95 hover:bg-slate-100 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-300 sm:h-6 sm:w-6"
                  >
                    -
                  </button>
                  <span className="min-w-7 px-1 text-center text-[13px] font-semibold text-slate-900 [font-variant-numeric:tabular-nums] sm:text-[14px]">
                    {formatQuantity(item.quantity)}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleAdjustQuantity(item, 1)}
                    disabled={isItemPending(item.id)}
                    data-no-swipe="true"
                    className="h-6 w-6 rounded-md border border-slate-200 bg-white text-sm font-semibold text-slate-700 transition active:scale-95 hover:bg-slate-100 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-300 sm:h-6 sm:w-6"
                  >
                    +
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => openEstimatedAmountEditor(item)}
                disabled={isItemPending(item.id)}
                className={`h-8 w-full rounded-lg border px-2 text-right text-[11px] font-semibold transition active:scale-[0.99] disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 sm:h-8 sm:text-xs [font-variant-numeric:tabular-nums] ${
                  editingEstimatedItemId === String(item.id)
                    ? 'border-blue-200 bg-blue-50 text-blue-800'
                    : 'border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100'
                }`}
              >
                {editingEstimatedItemId === String(item.id)
                  ? 'Editing...'
                  : (item.estimatedItemAmount !== null && item.estimatedItemAmount !== undefined
                    ? formatCurrency(item.estimatedItemAmount)
                    : 'Set est.')}
              </button>
            </div>
          </div>
        </div>

        {editingEstimatedItemId === String(item.id) && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitEstimatedAmountEditor(item);
            }}
            className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5"
          >
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={estimatedAmountDraft}
                onChange={(event) => setEstimatedAmountDraft(event.target.value)}
                placeholder="Estimated amount"
                autoFocus
                data-no-swipe="true"
                className="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-800 outline-none [font-variant-numeric:tabular-nums]"
              />
              <button
                type="submit"
                disabled={isItemPending(item.id)}
                data-no-swipe="true"
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                Save
              </button>
              <button
                type="button"
                onClick={cancelEstimatedAmountEditor}
                disabled={isItemPending(item.id)}
                data-no-swipe="true"
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {editingItemId === String(item.id) && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitItemNameEditor(item);
            }}
            className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5"
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={itemNameDraft}
                onChange={(event) => setItemNameDraft(event.target.value)}
                placeholder="Item name"
                autoFocus
                data-no-swipe="true"
                className="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-800 outline-none"
              />
              <button
                type="submit"
                disabled={isItemPending(item.id)}
                data-no-swipe="true"
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                Save
              </button>
              <button
                type="button"
                onClick={cancelItemNameEditor}
                disabled={isItemPending(item.id)}
                data-no-swipe="true"
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <p className="text-gray-600">Loading list...</p>;
  }

  return (
    <div className="-mt-2 space-y-3 pb-36 sm:mt-0 sm:space-y-4 sm:pb-0">
      <div
        className={`fixed left-3 right-3 top-[72px] z-20 transition-all duration-150 sm:hidden ${
          showScrollActions ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'
        }`}
      >
        <div className="rounded-xl border border-gray-200 bg-white/95 p-2 shadow-lg backdrop-blur">
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => navigate('/shopping-lists')}
              className="h-9 rounded-lg border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleResetChecklist}
              className="h-9 rounded-lg border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleDeleteList}
              className="h-9 rounded-lg border border-red-300 px-2 text-sm text-red-700 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-3">
        <div className="flex items-start justify-between gap-2 sm:items-center">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/shopping-lists')}
              aria-label="Back to shopping lists"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-800"
            >
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <h1 className="truncate text-lg font-semibold text-gray-900 sm:text-2xl">
              {shoppingList?.name || 'Shopping List'}
            </h1>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setShowSortMenu((current) => !current);
                }}
                title={`Sort: ${activeSortLabel}`}
                aria-label={`Sort items (${activeSortLabel})`}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <ArrowsUpDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              {showSortMenu && (
                <div
                  className="absolute right-0 top-9 z-30 min-w-[190px] rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg"
                  onClick={(event) => event.stopPropagation()}
                >
                  {itemSortOptions.map((option) => {
                    const isActive = itemSortMode === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setItemSortMode(option.value);
                          setShowSortMenu(false);
                        }}
                        className={`block w-full rounded-md px-2 py-1.5 text-left text-xs font-medium ${
                          isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleResetChecklist}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-gray-300 px-2 text-xs text-gray-700 hover:bg-gray-50"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Reset</span>
            </button>
            <button
              type="button"
              onClick={handleDeleteList}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-red-300 px-2 text-xs text-red-700 hover:bg-red-50"
            >
              <TrashIcon className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Delete</span>
            </button>
          </div>
        </div>
        {shoppingList?.isAutoGenerated && hasEstimatedAmounts && (
          <p className="mt-2 text-xs font-semibold text-slate-600">
            Estimated total: <span className="text-slate-900">{formatCurrency(estimatedListTotal)}</span>
          </p>
        )}
      </div>

      {false && (
        <section className={`overflow-hidden rounded-[22px] border shadow-sm sm:rounded-[28px] ${overallStrategyMeta.panelClassName}`}>
          <div
            className={`${showStrategyCard ? 'border-b border-white/60' : ''} cursor-pointer px-3 py-2.5 sm:px-5 sm:py-5`}
            onClick={() => setShowStrategyCard((current) => !current)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setShowStrategyCard((current) => !current);
              }
            }}
            role="button"
            tabIndex={0}
            aria-expanded={showStrategyCard}
            aria-label={showStrategyCard ? 'Collapse shopping strategy card' : 'Expand shopping strategy card'}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {showStrategyCard && (
                  <div className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${overallStrategyMeta.badgeClassName}`}>
                    Shopping with Strategy
                  </div>
                )}
                <p className={`${showStrategyCard ? 'mt-2' : 'mt-0.5'} max-w-2xl text-sm leading-5 text-slate-600 sm:leading-6`}>
                  {strategyHeadline.detail}
                </p>
              </div>

              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setShowStrategyCard((current) => !current);
                }}
                aria-expanded={showStrategyCard}
                aria-label={showStrategyCard ? 'Collapse shopping strategy card' : 'Expand shopping strategy card'}
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-slate-700 transition"
              >
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform duration-200 ${showStrategyCard ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>
            </div>

          </div>

          {showStrategyCard && (
            <>
              <div className="px-3 pb-3.5 sm:px-5 sm:pb-5">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div className="rounded-2xl border border-white/70 bg-white/90 px-3 py-2.5 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Budget</p>
                    <p className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">
                      {strategyOverall?.hasBudget ? formatCurrency(strategyOverall?.budgetAmount || 0) : '--'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/70 bg-white/90 px-3 py-2.5 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Spent so far</p>
                    <p className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">
                      {strategyOverall?.hasBudget ? formatCurrency(strategyOverall?.spentAmount || 0) : '--'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/70 bg-white/90 px-3 py-2.5 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Cart</p>
                    <p className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">
                      {formatCurrency(strategyListTotals?.estimatedTotal || 0)}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/70 bg-white/90 px-3 py-2.5 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Remaining</p>
                    <p className={`mt-1 text-base font-semibold sm:text-lg ${overallStrategyMeta.accentClassName}`}>
                      {strategyOverall?.hasBudget ? formatCurrency(strategyOverall?.projectedRemaining || 0) : '--'}
                    </p>
                  </div>
                </div>
                {overBudgetCategoryNames.length > 0 && (
                  <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50/90 px-3 py-2 text-sm font-medium text-rose-700">
                    This purchase could put {overBudgetCategoryNames.join(', ')} over budget.
                  </div>
                )}
                {strategySummary && strategySummary.categoriesWithBudgetsCount > 0 && (
                  <div className="mt-3 rounded-2xl border border-white/70 bg-white/90 px-3 py-2 text-sm text-slate-700">
                    Tracking {strategySummary.categoriesWithBudgetsCount} budgeted categor{strategySummary.categoriesWithBudgetsCount === 1 ? 'y' : 'ies'} for this trip.
                  </div>
                )}
              </div>
              {strategyTrimPlan && (
            <div className="border-b border-white/60 bg-white/75 px-4 py-4 sm:px-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Fastest way to rebalance</p>
                  <p className="mt-1 text-sm text-slate-600">
                    {strategyOverall?.status === 'over-budget'
                      ? `These items cover about ${formatCurrency(strategyTrimPlan.covered)} if you need to get back under budget quickly.`
                      : `These items free up about ${formatCurrency(strategyTrimPlan.covered)} if you want a little more cushion.`}
                  </p>
                </div>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                {strategyTrimPlan.picks.map((item) => (
                  <div key={`trim-pick-${item.id}`} className="rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
                    <p className="truncate text-sm font-semibold text-slate-900">{item.itemName}</p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {item.mainCategoryName || 'Uncategorized'} · Qty {formatQuantity(item.quantity)}
                    </p>
                    <p className="mt-2 text-base font-semibold text-slate-900">{formatCurrency(item.estimatedItemAmount || 0)}</p>
                  </div>
                ))}
              </div>
            </div>
              )}

              {showStrategyDetails && (
            <div className="bg-white/80 px-4 py-4 sm:px-5 sm:py-5">
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
                <div className="rounded-2xl border border-slate-200 bg-white p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">Money flow</p>
                    <p className="text-xs text-slate-500">Before and after this trip</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                      <span className="text-sm text-slate-600">Budget left now</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {strategyOverall?.hasBudget ? formatCurrency(strategyOverall?.remainingBeforeTrip || 0) : '--'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                      <span className="text-sm text-slate-600">This cart</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {formatCurrency(strategyListTotals?.estimatedTotal || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-slate-900 px-3 py-3 text-white">
                      <span className="text-sm font-medium">Projected after checkout</span>
                      <span className="text-base font-semibold">
                        {strategyOverall?.hasBudget ? formatCurrency(strategyOverall?.projectedRemaining || 0) : '--'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">Category impact</p>
                    <p className="text-xs text-slate-500">Top pressure points</p>
                  </div>
                  <div className="space-y-2">
                    {strategyCategories.slice(0, 5).map((category) => {
                      const categoryMeta = getStrategyStatusMeta(category.status);
                      return (
                        <div
                          key={`strategy-category-${category.mainCategoryName}`}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-sm font-semibold text-slate-900">{category.mainCategoryName}</p>
                                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${categoryMeta.badgeClassName}`}>
                                  {categoryMeta.label}
                                </span>
                              </div>
                              <p className="mt-1 text-[11px] text-slate-500">
                                {category.budgetAmount > 0
                                  ? `${formatCurrency(category.spentAmount)} spent of ${formatCurrency(category.budgetAmount)}`
                                  : 'No category budget set'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-slate-900">{formatCurrency(category.listEstimatedAmount)}</p>
                              <p className={`text-[11px] font-medium ${categoryMeta.accentClassName}`}>
                                {category.budgetAmount > 0
                                  ? (category.status === 'over-budget'
                                    ? `${formatCurrency(category.projectedOverspend)} over`
                                    : `${formatCurrency(category.projectedRemaining)} left`)
                                  : 'Set budget'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
              )}
            </>
          )}
        </section>
      )}

      <section ref={addSectionRef} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-3">
        <form onSubmit={handleAddItem} className="space-y-2">
          <div className="flex gap-2">
            <input
              ref={addInputRef}
              type="text"
              value={newItemName}
              onChange={(event) => setNewItemName(event.target.value)}
              placeholder="Add item or search history"
              className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 px-3 text-sm"
            />
            <button
              type="submit"
              disabled={addingItem || !String(parsedManualDraft.itemName || '').trim()}
              className="h-10 shrink-0 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-blue-400"
            >
              {addingItem ? 'Adding...' : 'Add'}
            </button>
          </div>

          <div className="flex items-center justify-between gap-2 px-0.5">
            <p className="text-[11px] text-slate-500">
              {parsedManualDraft.itemName
                ? `Qty ${formatQuantity(smartAddPreviewQuantity)}`
                : 'Tip: type "3 bananas" to parse quantity automatically.'}
            </p>
            <button
              type="button"
              onClick={() => setShowAddItemForm((current) => !current)}
              className="shrink-0 text-xs font-semibold text-blue-700 hover:text-blue-800"
            >
              {showAddItemForm ? 'Less' : 'More'}
            </button>
          </div>

          {showAddItemForm && (
            <div className="grid grid-cols-1 gap-2 rounded-lg bg-slate-50 p-2.5 sm:grid-cols-[92px_140px_auto]">
              <input
                type="number"
                min="1"
                step="1"
                value={newItemQuantity}
                onChange={(event) => setNewItemQuantity(event.target.value)}
                placeholder="Qty"
                className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm"
              />
              <input
                type="number"
                min="0"
                step="0.01"
                value={newItemEstimatedAmount}
                onChange={(event) => setNewItemEstimatedAmount(event.target.value)}
                placeholder="Est. Amount"
                className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  setShowAddItemForm(false);
                  setNewItemQuantity('');
                  setNewItemEstimatedAmount('');
                }}
                className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          )}
        </form>

        {String(parsedManualDraft.itemName || '').trim() ? (
          <div className="mt-2 space-y-1.5">
            <div className="space-y-1.5">
              <button
                type="button"
                onClick={() => handleAddItem({ preventDefault() {} })}
                disabled={addingItem}
                className="flex w-full items-center justify-between rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2 text-left hover:bg-blue-50 disabled:opacity-60"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">{`Add "${parsedManualDraft.itemName}"`}</p>
                  <p className="text-[11px] text-slate-500">
                    {`Qty ${formatQuantity(smartAddPreviewQuantity)}`}
                    {String(newItemEstimatedAmount || '').trim() ? ` • ${formatCurrency(newItemEstimatedAmount)}` : ''}
                  </p>
                </div>
                <span className="text-xs font-semibold text-blue-700">{addingItem ? 'Adding...' : 'Add'}</span>
              </button>

              {loadingSmartHistoryItems ? (
                <p className="px-1 py-1 text-xs text-slate-500">Searching history...</p>
              ) : smartHistoryItems.length > 0 ? (
                smartHistoryItems.map((item) => {
                  const itemName = String(item.itemName || '').trim();
                  const itemKey = `${String(item.normalizedItemName || itemName)}::${Math.max(1, Number(item.quantity || 1))}`;
                  const isAdding = addingHistoryItemKey === itemKey;
                  const detailParts = [`Qty ${formatQuantity(item.quantity || 1)}`];
                  if (item.lastPurchasedAt) {
                    detailParts.push(`Last ${formatShortDate(item.lastPurchasedAt)}`);
                  }
                  if (item.estimatedItemAmount !== null && item.estimatedItemAmount !== undefined) {
                    detailParts.push(formatCurrency(item.estimatedItemAmount));
                  }

                  return (
                    <button
                      key={`smart-history-item-${itemKey}`}
                      type="button"
                      onClick={() => handleAddFromHistory(item)}
                      disabled={Boolean(addingHistoryItemKey)}
                      className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left hover:border-blue-300 hover:bg-blue-50 disabled:opacity-60"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{itemName}</p>
                        <p className="truncate text-[11px] text-slate-500">{detailParts.join(' • ')}</p>
                      </div>
                      <span className="shrink-0 text-xs font-semibold text-blue-700">{isAdding ? 'Adding...' : 'Use'}</span>
                    </button>
                  );
                })
              ) : (
                <p className="px-1 py-1 text-xs text-slate-500">No history matches. Add it as a new item.</p>
              )}
            </div>
          </div>
        ) : null}
      </section>

      {!shoppingList?.items?.length ? (
        <p className="rounded border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
          This list is empty. Add an item to start building your list.
        </p>
      ) : (
        <div className="space-y-2.5 sm:space-y-2">
          <div className="grid grid-cols-[minmax(0,1fr)_84px_78px] items-center gap-2 px-3 pl-[34px] sm:grid-cols-[minmax(0,1fr)_96px_88px] sm:pl-[62px] text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-[11px]">
            <p>Item</p>
            <p className="text-center">Quantity</p>
            <p className="whitespace-nowrap text-right">Est. Amount</p>
          </div>
          {uncheckedVisibleItems.length > 0
            ? uncheckedVisibleItems.map((item) => renderShoppingListItem(item, { draggable: allowManualReorder && !reordering && pendingItemIds.length === 0 }))
            : (
              <p className="rounded-xl border border-dashed border-gray-300 px-4 py-5 text-center text-sm text-gray-500">
                All items are checked.
              </p>
            )}

          {checkedVisibleItems.length > 0 && (
            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setShowCheckedItems((current) => !current)}
                className="flex w-full items-center justify-between px-4 py-3 text-left"
                aria-expanded={showCheckedItems}
                aria-label={showCheckedItems ? 'Collapse checked items' : 'Expand checked items'}
              >
                <div>
                  <p className="text-sm font-semibold text-slate-900">Checked Items</p>
                  <p className="text-xs text-slate-500">{checkedVisibleItems.length} completed</p>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <span className="text-[11px] font-semibold">{showCheckedItems ? 'Hide' : 'Show'}</span>
                  <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${showCheckedItems ? 'rotate-180' : ''}`} aria-hidden="true" />
                </div>
              </button>

              {showCheckedItems && (
                <div className="space-y-2 border-t border-slate-200 p-3">
                  {checkedVisibleItems.map((item) => renderShoppingListItem(item))}
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {strategySection}

      <div className="fixed bottom-[calc(64px+env(safe-area-inset-bottom)+8px)] left-3 right-3 z-20 sm:static sm:mt-3">
        <div className="rounded-xl border border-slate-200 bg-white/95 px-3 py-2.5 text-sm shadow-lg backdrop-blur sm:bg-white sm:py-2 sm:shadow-sm">
          <div className="flex items-start justify-between gap-3 sm:items-center">
            <p className="min-w-0 text-slate-600">Total Items: <span className="font-semibold text-slate-900">{formatQuantity(totalListQuantity)}</span></p>
            <div className="shrink-0 text-right">
              <p className="flex items-center justify-end gap-2 text-slate-600">
                <span>Est. Amount</span>
                <span className="font-semibold text-slate-900">{hasEstimatedAmounts ? formatCurrency(estimatedListTotal) : '-'}</span>
              </p>
              <p className="text-[11px] text-slate-500">(without tax)</p>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={scrollToAddSection}
        aria-label="Add item"
        className={`fixed bottom-[calc(118px+env(safe-area-inset-bottom))] right-4 z-30 inline-flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-all duration-200 hover:bg-blue-700 sm:hidden ${
          showFloatingAddButton ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-3 opacity-0'
        }`}
      >
        <PlusIcon className="h-6 w-6" aria-hidden="true" />
      </button>

      <StatusToast
        error={error}
        onClearError={() => setError('')}
      />

    </div>
  );
}

export default ShoppingListDetail;
